/**
 * README
 * This is an "internal" component meant to be used solely by other RC components.
 * Please don't use this directly. If it seems like you'd benefit from it please
 * reach out to #design-systems for help coordinating!
 */
import React, { cloneElement, useMemo } from 'react';
import classNames from 'classnames';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  limitShift,
} from '@floating-ui/react-dom-interactions';
// framer-motion v7+ requires React 18+ (we're on 17) so the options here VS their docs might be off until that can happen
import { motion, AnimatePresence } from 'framer-motion';
import mergeRefs from 'react-merge-refs';
import { FLOATING_UI_DOM_ID } from '@reverbdotcom/cadence/constants';

const DIALOG_ARROW_HEIGHT = 10; // This needs to match the $arrow-width param on the dialogs.arrow scss mixin
const DIALOG_PANEL_OFFSET = DIALOG_ARROW_HEIGHT;
const DIALOG_WINDOW_EDGE_PADDING = 5; // The space in px a panel is padded from the edge of the window

type FloatingUIPositions = 'bottom' | 'bottom-start' | 'bottom-end';

interface RCNonModalDialogBaseProps {
  id: string,
  isOpen: boolean,
  onOpenChange: () => void,
  sticky?: boolean,
  position?: FloatingUIPositions,
  anchor: JSX.Element,
  children,
  variant?: 'default' | 'guide-popover'; // future: 'tooltip' for an eventual dark background tooltip
  dismissOnOutsidePress?: boolean;
  focusOrder?: Array<'reference' | 'floating' | 'content'>;
  trapFocus?: boolean;
  /** Index of the focusOrder array that should receive initial focus. Defaults to 0. Use -1 to prevent initial focus. */
  initialFocus?: -1 | 0 | 1 | 2;
}

function ariaID(id) {
  return {
    label: `${id}-label`,
    description: `${id}-description`,
  };
}

function framerMotionConfig(overrides = {}) {
  return {
    // More: https://www.framer.com/docs/transition
    initial: {
      opacity: 0,
      y: '-0.5rem',
    },
    animate: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
      },
    },
    transition: {
      duration: 0.25,
      ease: 'easeOut',
    },
    ...overrides,
  };
}

function arrowSide(panelPlacement) {
  // the side of the anchor that the panel sits on comes from `placement`
  const [ panelSide ] = panelPlacement.split('-');

  // the arrow sits on that opposite side, so we remap it here.
  const arrowSideMapping = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  };

  return arrowSideMapping[panelSide];
}

function arrowPositionStyles(arrowData, panelPlacement) {
  return {
    top: arrowData?.y && `${arrowData?.y}px`,
    left: `${arrowData?.x}px`,

    // override defaults above based on position
    [arrowSide(panelPlacement)]: `-${DIALOG_ARROW_HEIGHT}px`,
  };
}

export function RCNonModalDialogBase({
  id,
  isOpen,
  onOpenChange,
  sticky,
  position,
  anchor,
  children,
  variant = 'default',
  dismissOnOutsidePress = true,
  focusOrder = ['reference', 'content'],
  trapFocus = true,
  initialFocus = 0,
}: RCNonModalDialogBaseProps) {
  const arrowRef = React.useRef(null);
  const labelId = ariaID(id).label;
  const descriptionId = ariaID(id).description;
  const open = isOpen;

  // Main config for libs "useFloating"
  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    update,
    placement,
    middlewareData,
  } = useFloating({
    open,
    onOpenChange,
    middleware: [
      offset(DIALOG_PANEL_OFFSET),
      flip(),
      shift({
        padding: {
          top: DIALOG_ARROW_HEIGHT,
          right: DIALOG_WINDOW_EDGE_PADDING,
          bottom: DIALOG_WINDOW_EDGE_PADDING,
          left: DIALOG_WINDOW_EDGE_PADDING,
        },
        mainAxis: true,
        crossAxis: true,
        limiter: sticky ? undefined : limitShift(),
      }),
      arrow({ element: arrowRef }),
    ],
    placement: position,
    whileElementsMounted: (r, f, u) => {
      // We only need to auto update if it's possible to do so. This check also ensures
      // functionality with enzyme `mount`s in tests.
      if (('ResizeObserver' in window)) {
        return autoUpdate(r, f, u);
      }

      return null;
    },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useRole(context, {
      role: 'dialog', // this should be changed when this does more than a popover-like thing.
    }),
    useDismiss(context, {
      enabled: true,
      escapeKey: true,
      outsidePressEvent: 'mousedown',
      outsidePress: dismissOnOutsidePress,
    }),
  ]);

  // Preserve the consumer's ref
  const ref = useMemo(() => mergeRefs([reference, (anchor as any).ref]), [
    reference,
    anchor,
  ]);

  const arrowCallback = React.useCallback(
    (node) => {
      arrowRef.current = node;
      update();
    },
    [update],
  );

  const classes = classNames(
    'rc-non-modal-dialog-base',
    { 'rc-non-modal-dialog-base--guide-popover': variant === 'guide-popover' },
  );

  if (anchor.type === React.Fragment) {
    // Fragments will render to the screen, but they lose their ref and the Popover UIs won't know what to anchor
    // themselves to, and the panel will end up in the top-left corner of the screen.
    throw new Error('Pass a single element to the `anchor` prop, Fragments or other arrays are not supported.');
  }

  return (
    <>
      {cloneElement(anchor, {
        'data-floating-ui-is-open': isOpen,
        ...getReferenceProps({ ref, ...anchor.props }),
      })}
      <FloatingPortal
        id={FLOATING_UI_DOM_ID}
      >
        <AnimatePresence>
          {open && (
            <FloatingFocusManager
              context={context}
              modal={trapFocus}
              order={focusOrder}
              returnFocus
              initialFocus={initialFocus}
            >
              <motion.div
                ref={floating}
                className={classes}
                style={{
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                }}
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...framerMotionConfig(variant === 'guide-popover' && { transition: { delay: 1.5 } })}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...getFloatingProps()}
              >
                {children}
                <div
                  className={`rc-non-modal-dialog-base__arrow rc-non-modal-dialog-base__arrow--${arrowSide(placement)}`}
                  ref={arrowCallback}
                  style={arrowPositionStyles(middlewareData?.arrow, placement)}
                />
              </motion.div>
            </FloatingFocusManager>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </>
  );
}
